import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { Shell } from '../components/Shell.tsx'
import { bankDateRange } from '../lib/bank-import/dates.ts'
import { applyBankDuplicateWarnings } from '../lib/bank-import/duplicates.ts'
import { parseBankCsv, BankCsvValidationError, MAX_BANK_CSV_BYTES } from '../lib/bank-import/csv.ts'
import { sha256Hex } from '../lib/bank-import/fingerprint.ts'
import { matchBankRow, matchBankRows, refreshBankBucket } from '../lib/bank-import/matching.ts'
import type {
  BankBucket,
  BankImportMember,
  BankPreviewRow,
  CsvFieldError,
} from '../lib/bank-import/types.ts'
import { formatCents, formatDate } from '../lib/format.ts'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'
import { Require } from './guards.tsx'

type ImportState = 'idle' | 'parsing' | 'preview' | 'queued' | 'confirmed' | 'failed'

const BUCKETS: readonly { key: BankBucket; label: string }[] = [
  { key: 'duplicate', label: 'Possible duplicates' },
  { key: 'high', label: 'High confidence' },
  { key: 'low', label: 'Low confidence' },
  { key: 'unmatched', label: 'No match' },
  { key: 'skipped', label: 'Skipped' },
]

function rawFields(row: BankPreviewRow): string {
  return [row.reference, row.particulars, row.analysisCode, row.description]
    .filter(Boolean)
    .join(' · ')
}

function BankImportInner() {
  const z = useZero()
  const navigate = useNavigate()
  const [members, membersResult] = useQuery(queries.importMemberCandidates())
  const matchMembers = useMemo<readonly BankImportMember[]>(
    () =>
      members.map((member) => ({
        id: member.id,
        displayName: member.displayName,
        fullName: member.private?.fullName,
      })),
    [members],
  )
  const [fileName, setFileName] = useState('')
  const [fileSize, setFileSize] = useState(0)
  const [fileHash, setFileHash] = useState('')
  const [fileInputKey, setFileInputKey] = useState(0)
  const [rows, setRows] = useState<BankPreviewRow[] | null>(null)
  const [csvErrors, setCsvErrors] = useState<CsvFieldError[]>([])
  const [error, setError] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [importState, setImportState] = useState<ImportState>('idle')

  const range = useMemo(
    () => bankDateRange((rows ?? []).filter((row) => row.amountCents > 0)),
    [rows],
  )
  const [existingPayments, paymentsResult] = useQuery(
    range
      ? queries.bankTransferPaymentsForRange({ start: range.start, end: range.end })
      : false,
  )
  const [existingImports, importsResult] = useQuery(
    range
      ? queries.bankImportTransactionsForRange({ start: range.start, end: range.end })
      : false,
  )

  useEffect(() => {
    if (importState === 'queued' || importState === 'confirmed') return
    setRows((current) =>
      current?.map((row) => {
        if (row.manuallyMatched) return row
        const rematched = matchBankRow(row, matchMembers)
        return refreshBankBucket({
          ...rematched,
          removed: row.removed,
          skipReason: row.skipReason,
          resultStatus: row.resultStatus,
          resultMessage: row.resultMessage,
        })
      }) ?? null,
    )
  }, [importState, matchMembers])

  const previewRows = useMemo(
    () => applyBankDuplicateWarnings(rows ?? [], existingPayments ?? [], existingImports ?? []),
    [existingImports, existingPayments, rows],
  )
  const summary = useMemo(() => {
    const ready = previewRows.filter(
      (row) => row.amountCents > 0 && !row.removed && row.matchedUserId != null,
    )
    return {
      high: previewRows.filter((row) => row.bucket === 'high').length,
      low: previewRows.filter((row) => row.bucket === 'low').length,
      unmatched: previewRows.filter((row) => row.bucket === 'unmatched').length,
      duplicate: previewRows.filter((row) => row.bucket === 'duplicate').length,
      skipped: previewRows.filter((row) => row.bucket === 'skipped').length,
      ready: ready.length,
      totalCents: ready.reduce((sum, row) => sum + row.amountCents, 0),
    }
  }, [previewRows])
  const dataComplete =
    membersResult.type === 'complete' &&
    (!range ||
      (paymentsResult.type === 'complete' && importsResult.type === 'complete'))
  const busy = importState === 'parsing' || importState === 'queued'
  const locked = importState === 'queued' || importState === 'confirmed'

  async function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    setRows(null)
    setCsvErrors([])
    setError('')
    setStatusMessage('')
    setImportState('parsing')
    setFileName(file.name)
    setFileSize(file.size)

    if (!file.name.toLocaleLowerCase().endsWith('.csv')) {
      setImportState('failed')
      setError('Choose a .csv file')
      return
    }
    if (file.size > MAX_BANK_CSV_BYTES) {
      setImportState('failed')
      setError('CSV must be 2 MB or smaller')
      return
    }

    try {
      const text = await file.text()
      const [parsed, hash] = await Promise.all([parseBankCsv(text), sha256Hex(text)])
      setFileHash(hash)
      setRows(matchBankRows(parsed, matchMembers))
      setImportState('preview')
    } catch (caught) {
      setImportState('failed')
      if (caught instanceof BankCsvValidationError) {
        setCsvErrors(caught.errors)
        setError('The complete file was rejected. Fix the rows below and upload it again.')
      } else {
        setError(caught instanceof Error ? caught.message : 'Unable to parse the CSV')
      }
    }
  }

  function updateRow(rowId: string, update: (row: BankPreviewRow) => BankPreviewRow) {
    setRows((current) =>
      current?.map((row) =>
        row.id === rowId
          ? refreshBankBucket({
              ...update(row),
              resultStatus: 'preview',
              resultMessage: null,
            })
          : row,
      ) ?? null,
    )
    setImportState('preview')
    setError('')
    setStatusMessage('')
  }

  function selectMember(rowId: string, userId: string) {
    const member = matchMembers.find((candidate) => candidate.id === userId)
    updateRow(rowId, (row) => ({
      ...row,
      matchedUserId: member?.id ?? null,
      matchedUserName: member?.displayName ?? null,
      confidence: member ? 1 : 0,
      matchStatus: member ? 'matched' : 'unmatched',
      manuallyMatched: true,
    }))
  }

  function toggleRemoved(rowId: string) {
    updateRow(rowId, (row) => {
      if (row.amountCents <= 0) return row
      return row.removed
        ? { ...row, removed: false, skipReason: null }
        : { ...row, removed: true, skipReason: 'manual' }
    })
  }

  function updateResult(
    submittedIds: ReadonlySet<string>,
    status: BankPreviewRow['resultStatus'],
    message: string | null,
  ) {
    setRows((current) =>
      current?.map((row) =>
        submittedIds.has(row.id)
          ? { ...row, resultStatus: status, resultMessage: message }
          : row,
      ) ?? null,
    )
  }

  async function confirmImport() {
    if (!rows || !fileHash || locked) return
    if (!dataComplete) {
      setError('Wait for member and duplicate data to finish synchronizing')
      return
    }
    const unmatched = previewRows.filter(
      (row) => row.amountCents > 0 && !row.removed && row.matchedUserId == null,
    )
    if (unmatched.length > 0) {
      setError('Assign or remove every unmatched positive row before confirming')
      return
    }
    const ready = previewRows.filter(
      (row) => row.amountCents > 0 && !row.removed && row.matchedUserId != null,
    )
    if (ready.length === 0) {
      setError('There are no matched incoming payments to import')
      return
    }
    if (
      summary.duplicate > 0 &&
      !window.confirm(
        `${summary.duplicate} possible duplicate${summary.duplicate === 1 ? '' : 's'} will still be imported. Continue?`,
      )
    ) {
      return
    }

    const submittedIds = new Set(ready.map((row) => row.id))
    setError('')
    setImportState('queued')
    updateResult(submittedIds, 'queued', 'Queued for synchronization')
    setStatusMessage(
      `Reconciling ${ready.length} payment${ready.length === 1 ? '' : 's'} totaling ${formatCents(summary.totalCents)}…`,
    )

    try {
      const mutation = z.mutate(
        mutators.bankImports.confirm({
          batchId: crypto.randomUUID(),
          fileName,
          sourceSha256: fileHash,
          rows: ready.map((row) => ({
            transactionId: crypto.randomUUID(),
            paymentId: crypto.randomUUID(),
            creditId: crypto.randomUUID(),
            rowNumber: row.rowNumber,
            transactionDate: row.transactionDate,
            amountCents: row.amountCents,
            userId: row.matchedUserId!,
            matchConfidence: Math.round(row.confidence * 10_000),
            sourceFingerprint: row.sourceFingerprint,
            otherParty: row.otherParty,
            reference: row.reference,
            particulars: row.particulars,
            analysisCode: row.analysisCode,
            description: row.description,
            rawData: row.rawData,
          })),
        }),
      )
      const clientResult = await mutation.client
      if (clientResult.type === 'error') {
        setImportState('failed')
        updateResult(submittedIds, 'failed', clientResult.error.message)
        setError(clientResult.error.message)
        setStatusMessage('')
        return
      }

      setStatusMessage('Payments are visible locally and awaiting server confirmation')
      void mutation.server.then((serverResult) => {
        if (serverResult.type === 'success') {
          setImportState('confirmed')
          updateResult(submittedIds, 'created', null)
          setStatusMessage(
            `${ready.length} payment${ready.length === 1 ? '' : 's'} reconciled`,
          )
        } else {
          setImportState('failed')
          updateResult(submittedIds, 'failed', serverResult.error.message)
          setError(serverResult.error.message)
          setStatusMessage('')
        }
      })
    } catch {
      setImportState('failed')
      updateResult(submittedIds, 'failed', 'Failed to reconcile the statement')
      setError('Failed to reconcile the statement')
      setStatusMessage('')
    }
  }

  function reset() {
    setFileName('')
    setFileSize(0)
    setFileHash('')
    setFileInputKey((current) => current + 1)
    setRows(null)
    setCsvErrors([])
    setError('')
    setStatusMessage('')
    setImportState('idle')
  }

  return (
    <Shell title="Bank reconciliation" wide onBack={() => navigate({ to: '/admin' })}>
      <div className="card form-stack">
        <h2>Upload bank statement</h2>
        <p className="muted import-help">
          CSV headers: <strong>date</strong>, <strong>amount</strong>, and{' '}
          <strong>other-party</strong>. Reference, particulars, analysis code, and
          description are optional.
        </p>
        <label>
          Statement CSV
          <input
            key={fileInputKey}
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => void selectFile(event)}
            disabled={busy || importState === 'confirmed'}
          />
        </label>
        {fileName ? (
          <p className="file-summary">
            <strong>{fileName}</strong>
            <span>{(fileSize / 1024).toFixed(1)} KB</span>
          </p>
        ) : null}
        {importState === 'parsing' ? <p className="form-message">Parsing statement…</p> : null}
      </div>

      {error ? <p className="error">{error}</p> : null}
      {csvErrors.length > 0 ? (
        <div className="card validation-list">
          <h2>CSV errors ({csvErrors.length})</h2>
          {csvErrors.map((item, index) => (
            <div className="validation-item" key={`${item.rowNumber}-${item.field}-${index}`}>
              <strong>Row {item.rowNumber} · {item.field}</strong>
              <span>{item.message}</span>
            </div>
          ))}
        </div>
      ) : null}

      {rows ? (
        <>
          <div className="import-summary">
            <span>{summary.high} high confidence</span>
            <span>{summary.low} low confidence</span>
            <span>{summary.unmatched} no match</span>
            <span>{summary.duplicate} possible duplicate</span>
            <span>{summary.skipped} skipped</span>
            <span>{summary.ready} ready · {formatCents(summary.totalCents)}</span>
          </div>
          <p className="status-line">
            <span className={`dot ${dataComplete ? 'dot--on' : 'dot--off'}`} />
            {dataComplete
              ? 'Member, payment, and audit data are synchronized'
              : 'Showing cached data; confirmation unlocks when synchronization completes'}
          </p>

          {BUCKETS.map((bucket) => {
            const bucketRows = previewRows.filter((row) => row.bucket === bucket.key)
            if (bucketRows.length === 0) return null
            return (
              <section className="bank-bucket" key={bucket.key}>
                <h2>
                  {bucket.label} <span>{bucketRows.length}</span>
                </h2>
                <div className="card import-table-wrap">
                  <table className="import-table bank-import-table">
                    <thead>
                      <tr>
                        <th>Statement row</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Member</th>
                        <th>Match</th>
                        <th>Result</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bucketRows.map((row) => (
                        <tr
                          key={row.id}
                          className={[
                            row.removed ? 'import-row--skipped' : '',
                            row.duplicateReasons.length > 0 ? 'import-row--duplicate' : '',
                            row.resultStatus === 'failed' ? 'import-row--invalid' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <td>
                            <strong>{row.otherParty}</strong>
                            <small>CSV row {row.rowNumber}</small>
                            {rawFields(row) ? <small>{rawFields(row)}</small> : null}
                            {row.duplicateReasons.map((reason) => (
                              <small className="warning-text" key={reason}>⚠ {reason}</small>
                            ))}
                            <details>
                              <summary>Raw row</summary>
                              <dl className="raw-data">
                                {Object.entries(row.rawData).map(([name, value]) => (
                                  <div key={name}>
                                    <dt>{name}</dt>
                                    <dd>{value || '—'}</dd>
                                  </div>
                                ))}
                              </dl>
                            </details>
                          </td>
                          <td>{formatDate(row.transactionDate)}</td>
                          <td className={row.amountCents < 0 ? 'negative-amount' : ''}>
                            {formatCents(row.amountCents)}
                          </td>
                          <td>
                            <select
                              aria-label={`Member for CSV row ${row.rowNumber}`}
                              value={row.matchedUserId ?? ''}
                              disabled={row.removed || locked}
                              onChange={(event) => selectMember(row.id, event.target.value)}
                            >
                              <option value="">Select member…</option>
                              {matchMembers.map((member) => (
                                <option value={member.id} key={member.id}>
                                  {member.displayName}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="import-confidence">
                            <span className={`match-status match-status--${row.matchStatus}`}>
                              {row.manuallyMatched ? 'manual' : row.matchStatus}
                            </span>
                            <small>{Math.round(row.confidence * 100)}%</small>
                          </td>
                          <td>
                            <span className={`result-status result-status--${row.resultStatus}`}>
                              {row.resultStatus}
                            </span>
                            {row.resultMessage ? <small>{row.resultMessage}</small> : null}
                            {row.skipReason ? (
                              <small>
                                {row.skipReason === 'not-incoming'
                                  ? 'Not incoming'
                                  : row.skipReason === 'zero'
                                    ? 'Zero amount'
                                    : 'Removed by admin'}
                              </small>
                            ) : null}
                          </td>
                          <td>
                            {row.amountCents > 0 ? (
                              <button
                                className={`btn btn-sm ${row.removed ? 'btn-success' : 'btn-secondary'}`}
                                type="button"
                                disabled={locked}
                                onClick={() => toggleRemoved(row.id)}
                              >
                                {row.removed ? 'Restore' : 'Remove'}
                              </button>
                            ) : (
                              <span className="muted">Skipped</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )
          })}

          <div className="card confirm-panel">
            <div>
              <strong>{summary.ready} payments · {formatCents(summary.totalCents)}</strong>
              <span>
                {summary.duplicate > 0
                  ? `${summary.duplicate} possible duplicate${summary.duplicate === 1 ? '' : 's'} included`
                  : 'No duplicate warnings'}
              </span>
            </div>
            <button
              className="btn btn-sm"
              type="button"
              disabled={
                locked ||
                !dataComplete ||
                summary.ready === 0 ||
                summary.unmatched > 0
              }
              onClick={() => void confirmImport()}
            >
              {importState === 'queued' ? 'Synchronizing…' : 'Confirm reconciliation'}
            </button>
          </div>
          {statusMessage ? <p className="form-message">{statusMessage}</p> : null}
          {importState === 'confirmed' ? (
            <button className="btn btn-secondary" type="button" onClick={reset}>
              Import another statement
            </button>
          ) : null}
        </>
      ) : null}
    </Shell>
  )
}

export function BankImportPage() {
  return (
    <Require roles={['admin']} showDenied>
      <BankImportInner />
    </Require>
  )
}
